import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MessageSquarePlus, StickyNote } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui/Skeleton'
import { Badge } from '@/components/ui/Badge'
import { useCan } from '@/lib/permissions'
import { formatDateTime, formatTimeAgo } from '@/lib/format'
import { ApiError } from '@/api/client'
import { RequestAccessButton } from '@/features/access-requests/RequestAccessButton'
import { addCaseNote, listCaseNotes } from '../api'
import type { CaseNote } from '../types'

// Mirrors the backend enum (entities.NoteType): comment / internal
// / customer_contact / system. `system` is reserved for platform-
// generated notes and not exposed to the composer; analysts choose
// between the three operator-visible values.
const NOTE_TYPES: Array<{ value: string; label: string; hint: string }> = [
  { value: 'comment', label: 'Comment', hint: 'General observation' },
  {
    value: 'internal',
    label: 'Internal',
    hint: 'Hidden from customer-facing surfaces',
  },
  {
    value: 'customer_contact',
    label: 'Customer contact',
    hint: 'Outreach record',
  },
]

const MIN_LENGTH = 3

/**
 * CaseNotesPanel — reads + writes the case-level notes feed.
 *
 * Sits in the case detail page main column. The composer respects
 * the cases:write permission; analysts without it see the feed
 * read-only with an inline hint.
 */
export function CaseNotesPanel({ caseId }: { caseId: string }) {
  const canWrite = useCan('cases:write')
  return (
    <Card id="case-notes">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <StickyNote size={14} className="text-primary" aria-hidden />
            Case notes
          </CardTitle>
          <p className="text-xs text-muted-fg">
            Anything worth handing off — observations, next steps, customer
            contact records — goes here.
          </p>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {canWrite ? <Composer caseId={caseId} /> : <ReadOnlyHint caseId={caseId} />}
        <NotesFeed caseId={caseId} />
      </CardContent>
    </Card>
  )
}

function ReadOnlyHint({ caseId }: { caseId: string }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-dashed border-border p-3 text-xs text-muted-fg">
      <span>
        You have read-only access to this case. Adding notes requires the{' '}
        <code>cases:write</code> permission.
      </span>
      <RequestAccessButton
        resourceType="case"
        resourceId={caseId}
        scope="cases:write"
      />
    </div>
  )
}

function Composer({ caseId }: { caseId: string }) {
  const qc = useQueryClient()
  const [content, setContent] = useState('')
  const [noteType, setNoteType] = useState('comment')

  const mutation = useMutation({
    mutationFn: () =>
      addCaseNote(caseId, { content: content.trim(), note_type: noteType }),
    onSuccess: (note) => {
      qc.setQueryData<CaseNote[] | undefined>(
        ['cases', caseId, 'notes'],
        (prev) => (prev ? [note, ...prev] : [note]),
      )
      void qc.invalidateQueries({ queryKey: ['cases', caseId, 'notes'] })
      setContent('')
      setNoteType('comment')
      toast.success('Note added')
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError
          ? err.message || 'Could not add the note.'
          : err instanceof Error
            ? err.message
            : 'Could not add the note.'
      toast.error(msg)
    },
  })

  const trimmed = content.trim()
  const valid = trimmed.length >= MIN_LENGTH

  return (
    <form
      className="flex flex-col gap-2 rounded-md border border-border bg-card p-3"
      onSubmit={(e) => {
        e.preventDefault()
        if (valid) mutation.mutate()
      }}
    >
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-muted-fg">
          Add a note (≥{MIN_LENGTH} chars)
        </span>
        <textarea
          aria-label="Case note body"
          rows={3}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="What did you observe, decide, or follow up on?"
          className="w-full resize-y rounded border border-input bg-card px-3 py-2 text-sm"
        />
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5 text-xs text-muted-fg">
          <span>Type</span>
          <select
            aria-label="Note type"
            value={noteType}
            onChange={(e) => setNoteType(e.target.value)}
            className="rounded border border-input bg-card px-2 py-1 text-xs"
          >
            {NOTE_TYPES.map((t) => (
              <option key={t.value} value={t.value} title={t.hint}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <span className="ml-auto text-[0.6875rem] text-muted-fg">
          {trimmed.length} chars
        </span>
        <Button
          type="submit"
          size="sm"
          disabled={!valid || mutation.isPending}
        >
          <MessageSquarePlus size={12} aria-hidden />
          {mutation.isPending ? 'Saving…' : 'Add note'}
        </Button>
      </div>
    </form>
  )
}

function NotesFeed({ caseId }: { caseId: string }) {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['cases', caseId, 'notes'],
    queryFn: () => listCaseNotes(caseId),
    staleTime: 30_000,
  })

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    )
  }
  if (isError) {
    return (
      <div className="flex items-center justify-between gap-2 rounded border border-status-failed/30 bg-status-failed/5 px-3 py-2 text-xs text-fg">
        <span>
          Couldn't load notes —{' '}
          {error instanceof Error ? error.message : 'unknown error'}.
        </span>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    )
  }
  const notes = data ?? []
  if (notes.length === 0) {
    return (
      <p className="rounded border border-dashed border-border p-3 text-xs text-muted-fg">
        No notes yet. Add the first one above — it'll show up at the top of
        this list.
      </p>
    )
  }
  // Newest first. Backend already orders DESC but we sort client-side
  // too so the optimistic insert from the composer always lands at the
  // top regardless of clock skew.
  const ordered = [...notes].sort((a, b) =>
    a.created_at < b.created_at ? 1 : -1,
  )
  return (
    <ol className="flex flex-col gap-2">
      {ordered.map((n) => (
        <NoteRow key={n.id} note={n} />
      ))}
    </ol>
  )
}

function NoteRow({ note }: { note: CaseNote }) {
  const typeMeta = NOTE_TYPES.find((t) => t.value === note.note_type)
  // Backend hydrates `author` on every row, but legacy / partial
  // wire shapes (and earlier MSW fixtures) may omit it. Fall back
  // gracefully so a single missing block doesn't blank the panel.
  const initials = note.author?.initials || '?'
  const displayName =
    note.author?.display_name ||
    (note.author_id ? `User #${note.author_id}` : 'Unknown')
  return (
    <li className="rounded-md border border-border bg-card p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[0.6875rem] font-semibold text-primary"
          aria-hidden
        >
          {initials}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">
            {displayName}
          </p>
          <p className="truncate text-[0.6875rem] text-muted-fg" title={formatDateTime(note.created_at)}>
            {formatTimeAgo(note.created_at)} · {formatDateTime(note.created_at)}
          </p>
        </div>
        {typeMeta && note.note_type !== 'comment' ? (
          <Badge
            variant={note.note_type === 'internal' ? 'pending' : 'info'}
            className="capitalize"
          >
            {typeMeta.label}
          </Badge>
        ) : null}
        {note.is_internal && note.note_type !== 'internal' ? (
          <Badge variant="pending">Internal</Badge>
        ) : null}
      </div>
      <p className="mt-2 whitespace-pre-wrap text-sm leading-snug">
        {note.content}
      </p>
    </li>
  )
}
